import {ICountryArray, countryArray} from 'data/country';
import React, {useRef, useState} from 'react';
import {styled, css} from 'styled-components';
import DefaultInput from './DefaultInput';
import DefaultText from './DefaultText';
import {changeLocaleType, formatPhoneNumber} from 'utils/fomatPhoneNumber';
import {CountryCode} from 'libphonenumber-js';
import DefaultImageView from './DefaultImageView';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import {useOutSideRef} from 'utils/useOutSideRef';
import {min} from 'moment-timezone';

/**
 * 전화번호 입력 컴포넌트의 Props 타입
 * @property {string} label - 라벨 텍스트
 * @property {ICountryArray} selectCountry - 선택된 국가
 * @property {any} [setSelectCountry] - 국가 설정 함수
 * @property {string} phoneNumber - 전화번호
 * @property {(value: string) => void} [setPhoneNumber] - 전화번호 설정 함수
 * @property {React.CSSProperties} [selectWrapStyle] - 선택 래퍼 스타일
 * @property {React.ReactNode} [timer] - 타이머 컴포넌트
 * @property {() => void} [onClickInput] - 입력 필드 클릭 핸들러
 * @property {React.ReactNode} [sendAuth] - 인증 전송 컴포넌트
 * @property {boolean} [isRow] - 가로 배치 여부
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {boolean} [nonEditable] - 편집 불가 여부
 * @property {boolean} [edit] - 편집 모드 여부
 * @property {() => void} [onClickEdit] - 편집 버튼 클릭 핸들러
 * @property {boolean} [errorBorder] - 에러 테두리 표시 여부
 * @property {string} [rightText] - 우측 텍스트
 * @property {() => void} [onClickRightText] - 우측 텍스트 클릭 핸들러
 * @property {React.CSSProperties} [wrapperStyle] - 래퍼 스타일
 * @property {React.CSSProperties} [inputStyle] - 입력 필드 스타일
 * @property {string} [rightTextTooltip] - 우측 텍스트 툴팁
 */
type Props = {
  label: string;
  selectCountry: ICountryArray;
  setSelectCountry?: any;
  phoneNumber: string;
  setPhoneNumber?: (value: string) => void;
  selectWrapStyle?: React.CSSProperties;
  timer?: React.ReactNode;
  onClickInput?: () => void;
  sendAuth?: React.ReactNode;
  isRow?: boolean;
  labelStyle?: React.CSSProperties;
  readOnly?: boolean;
  nonEditable?: boolean;
  edit?: boolean;
  onClickEdit?: () => void;
  errorBorder?: boolean;
  rightText?: string;
  onClickRightText?: () => void;
  wrapperStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  rightTextTooltip?: string;
};

/**
 * 전화번호 입력 컴포넌트
 * 국가 선택과 전화번호 입력을 제공하는 컴포넌트입니다.
 * @param {Props} props - 전화번호 입력 컴포넌트의 props
 * @returns {JSX.Element} 전화번호 입력 컴포넌트
 */
const PhoneNumberInput = ({
  label,
  selectCountry,
  setSelectCountry,
  phoneNumber,
  setPhoneNumber,
  selectWrapStyle,
  timer,
  onClickInput,
  sendAuth,
  isRow,
  labelStyle,
  readOnly,
  nonEditable,
  edit,
  onClickEdit,
  errorBorder,
  rightText,
  onClickRightText,
  wrapperStyle,
  inputStyle,
  rightTextTooltip,
}: Props) => {
  // locale
  const fomattedlocale: CountryCode = changeLocaleType(
    selectCountry.countryCode,
  );

  const [isOpenSelect, setIsOpenSelect] = useState(false);
  const [onEditTooltip, setOnEditTooltip] = useState(false);

  const ref = useRef(null);
  useOutSideRef(ref, () => setIsOpenSelect(false));

  return (
    <LabelInput $isRow={isRow ? 1 : 0}>
      <DefaultText
        text={label}
        textStyle={{
          width: isRow ? '20%' : 'auto',
          marginBottom: isRow ? 0 : minmax(12),
          paddingRight: '2%',
          ...labelStyle,
        }}
      />
      <InputContainer>
        <div style={{width: '100%', height: minmax(60)}}>
          <InputInner>
            <Select
              ref={ref}
              style={{
                backgroundColor: nonEditable
                  ? colors.whiteSmoke
                  : 'transparent',
                cursor: readOnly ? 'default' : 'pointer',
                ...selectWrapStyle,
              }}
              onClick={() => setIsOpenSelect(!isOpenSelect)}>
              <SelectInner>
                <DefaultText
                  text={selectCountry.code}
                  textStyle={{
                    color: nonEditable ? colors.darkGray : colors.black,
                  }}
                />
                <DefaultImageView
                  source={`images/common/${
                    nonEditable
                      ? 'phone_number_input_down_gray'
                      : 'phone_number_input_down'
                  }.png`}
                  style={{
                    width: minmax(20),
                    transform: isOpenSelect ? 'rotate(180deg)' : '',
                    // transition: 'transform 150ms ease',
                    opacity: readOnly ? 0 : 1,
                  }}
                />
              </SelectInner>
              {!readOnly && isOpenSelect && (
                <OptionContainer onClick={(e: any) => e.stopPropagation()}>
                  {countryArray.map((item: any) => (
                    <DefaultText
                      key={item.code}
                      text={item.code}
                      onClick={() => {
                        if (setSelectCountry) {
                          setSelectCountry(item);
                          setIsOpenSelect(false);
                        }
                      }}
                      textStyle={{
                        padding: '10% 20%',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    />
                  ))}
                </OptionContainer>
              )}
            </Select>

            <InputWrap>
              <DefaultInput
                placeholderText="enterID"
                value={formatPhoneNumber({
                  phone: phoneNumber,
                  locale: selectCountry.languageCode,
                })}
                setValue={(value: any) =>
                  setPhoneNumber && setPhoneNumber(value.replace(/\D/g, ''))
                }
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  ...wrapperStyle,
                }}
                inputStyle={{
                  border: errorBorder ? '1px solid rgb(230, 30, 22)' : '',
                  backgroundColor: nonEditable
                    ? colors.whiteSmoke
                    : 'transparent',
                  color: nonEditable ? colors.darkGray : colors.black,
                  paddingRight: rightText || edit ? '20%' : minmax(20),
                  ...inputStyle,
                }}
                onClick={onClickInput}
                readOnly={readOnly}
              />
              {edit && (
                <DefaultText
                  text="change"
                  textStyle={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'absolute',
                    top: 0,
                    right: minmax(20),
                    color: colors.primary,
                    cursor: 'pointer',
                  }}
                  onClick={onClickEdit}
                  onMouseEnter={() => {
                    // 툴팁 표시
                    setOnEditTooltip(true);
                  }}
                  onMouseLeave={() => {
                    // 툴팁 사라짐
                    setOnEditTooltip(false);
                  }}
                />
              )}
              {rightTextTooltip && onEditTooltip && (
                <DefaultText
                  text={rightTextTooltip || undefined}
                  textStyle={{
                    padding: '10px 15px',
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    border: `1px solid ${colors.borderGray}`,
                    borderRadius: minmax(10),
                    backgroundColor: colors.white,
                    zIndex: 100,
                  }}
                />
              )}
              {rightText && (
                <DefaultText
                  text={rightText}
                  textStyle={RightTextStyle}
                  onClick={onClickRightText}
                />
              )}
            </InputWrap>
          </InputInner>

          {timer}
        </div>

        {sendAuth}
      </InputContainer>
    </LabelInput>
  );
};

export default PhoneNumberInput;

const LabelInput = styled.div<{$isRow?: number}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  ${(props: any) =>
    props.$isRow &&
    css`
      flex-direction: row;
      align-items: center;
    `}
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: ${minmax(16)};
`;

const InputInner = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${minmax(16)};

  border-radius: ${minmax(10)};
  height: 100%;
`;

const SelectInner = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
  font-size: ${minmax(24)};
  flex: 1;
`;

const OptionContainer = styled.div`
  width: 100%;
  max-height: ${minmax(100)};
  overflow-y: scroll;
  position: absolute;
  border-radius: ${minmax(10)};
  border: solid 1px #cbcbcb;
  left: 0;
  top: 110%;
  z-index: 10;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;

  p:hover {
    background-color: #e8ecf0;
  }
`;

const Select = styled.div`
  width: ${minmax(109)};
  height: 100%;
  border-radius: ${minmax(10)};
  border: solid 1px #cbcbcb;
  display: flex;
  /* flex-shrink: 1; */
  position: relative;
  cursor: pointer;
`;

const InputWrap = styled.div`
  width: 100%;
  position: relative;
`;

const RightTextStyle: React.CSSProperties = {
  height: '100%',
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  color: colors.primary,
  paddingRight: 'max(12px, min(1.2vw, 24px))',
  cursor: 'pointer',
};
